<template>
  <div class="notification-container" :class="positionClass">
    <TransitionGroup name="notification-list" tag="div" class="notification-list">
      <NotificationToast
        v-for="notification in notificationStore.activeNotifications"
        :key="notification.id"
        :type="notification.type"
        :title="notification.title"
        :message="notification.message"
        :duration="notification.duration"
        :auto-close="notification.autoClose"
        :dismissible="notification.dismissible"
        :persistent="notification.persistent"
        @close="notificationStore.removeNotification(notification.id)"
      />
    </TransitionGroup>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { useNotificationStore } from '../stores/notifications'
import { storeToRefs } from 'pinia'
import NotificationToast from './NotificationToast.vue'

const props = defineProps({
  position: {
    type: String,
    default: 'top-right',
    validator: (value) => [
      'top-left', 'top-center', 'top-right',
      'bottom-left', 'bottom-center', 'bottom-right',
      'center'
    ].includes(value)
  },
  maxNotifications: {
    type: Number,
    default: 5
  }
})

const notificationStore = useNotificationStore()

const positionClass = computed(() => `notification-container--${props.position}`)

// Auto-remove old notifications if too many
const { activeNotifications } = storeToRefs(notificationStore)
watch(activeNotifications, (notifications) => {
  if (notifications.length > props.maxNotifications) {
    // Remove oldest notifications
    const excess = notifications.length - props.maxNotifications
    const oldestNotifications = notifications
      .slice()
      .sort((a, b) => a.timestamp - b.timestamp)
      .slice(0, excess)
    
    oldestNotifications.forEach(notification => {
      notificationStore.removeNotification(notification.id)
    })
  }
}, { immediate: true })
</script>

<style scoped>
.notification-container {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  max-width: 90vw;
}

.notification-container--top-left {
  top: 20px;
  left: 20px;
}

.notification-container--top-center {
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
}

.notification-container--top-right {
  top: 20px;
  right: 20px;
}

.notification-container--bottom-left {
  bottom: 20px;
  left: 20px;
}

.notification-container--bottom-center {
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
}

.notification-container--bottom-right {
  bottom: 20px;
  right: 20px;
}

.notification-container--center {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.notification-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: auto;
}

/* Adjust stacking for bottom positions */
.notification-container--bottom-left .notification-list,
.notification-container--bottom-center .notification-list,
.notification-container--bottom-right .notification-list {
  flex-direction: column-reverse;
}

/* Transition animations */
.notification-list-enter-active,
.notification-list-leave-active {
  transition: all 0.3s ease;
}

.notification-list-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-list-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-list-move {
  transition: transform 0.3s ease;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .notification-container {
    max-width: calc(100vw - 32px);
  }
  
  .notification-container--top-left,
  .notification-container--bottom-left {
    left: 16px;
  }
  
  .notification-container--top-right,
  .notification-container--bottom-right {
    right: 16px;
  }
  
  .notification-container--top-center,
  .notification-container--bottom-center {
    left: 50%;
    transform: translateX(-50%);
  }
}
</style> 